/**
 * Vista read-only del plano de mesas del tenant para seleccionar mesa al
 * crear un pedido con origen `table`. Lee la config persistida por el
 * admin en `tenant_features.configJson` (ver `TablesConfig.tsx`) y pinta
 * el plano igual, pero:
 *   - Las mesas NO se arrastran.
 *   - Click = selección.
 *   - Mesas con `blocked:true` se muestran tachadas y no son seleccionables.
 *
 * Si el tenant aún no configuró mesas, muestra un fallback.
 */

import { Button } from '@atreus/ui';
import { Link } from '@tanstack/react-router';
import { ConciergeBell, Lock, Settings, Users } from 'lucide-react';
import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';

import { trpc } from '../../lib/trpc.js';

import {
  ASPECT_RATIO,
  CHAIR_GAP,
  CHAIR_LONG,
  CHAIR_SHORT,
  VIRTUAL,
  boundingSize,
  chairCounts,
  parseConfig,
  plateSize,
  type TablesTable,
  type TablesZone,
} from './floorPlan.js';

// ---------- Public component ----------

export interface SelectedTableInfo {
  id: string;
  label: string;
  capacity: number;
  zoneName: string;
}

export const TablesFloorPlanPicker = ({
  value,
  valueById,
  onSelect,
  readOnly = false,
  busyCountByTableId,
}: {
  /** Label de la mesa a resaltar. Sólo se usa si `valueById` no matchea. */
  value: string | null;
  /**
   * ID exacto de la mesa (UUID). Cuando está presente tiene prioridad
   * sobre `value` para desambigüar labels repetidos en zonas distintas.
   * Se introduce con la migración 0003 que persiste `orders.table_id`.
   */
  valueById?: string | null;
  /**
   * Obligatorio en modo interactivo (default). Ignorado cuando `readOnly`
   * porque el picker solo muestra la ubicación resaltada.
   */
  onSelect?: (selection: SelectedTableInfo | null) => void;
  readOnly?: boolean;
  /**
   * Conteo de pedidos activos por `tableId`. Se usa para superponer un
   * chip "Ocupada · N" sobre mesas con tickets en curso, como pista al
   * operador que ya hay un pedido en esa mesa (ej. a la hora de abrir
   * uno nuevo para una ronda de bebidas). No bloquea la selección —
   * puede ser legítimo abrir otro ticket en paralelo.
   */
  busyCountByTableId?: Map<string, number>;
}) => {
  const tenant = trpc.tenant.current.useQuery();
  const raw = tenant.data?.features.find((f) => f.featureId === 'tables')
    ?.configJson;
  const config = useMemo(() => parseConfig(raw), [raw]);

  // Cuando llega `valueById` o `value`, arrancamos en la zona que contiene
  // esa mesa para que el usuario la vea sin adivinar en qué tab está.
  // Prioridad: match por ID > match por label.
  const initialZone = useMemo(() => {
    if (!config) return '';
    if (valueById) {
      const byId = config.zones.find((z) =>
        z.tables.some((t) => t.id === valueById),
      );
      if (byId) return byId.id;
    }
    if (value) {
      const byLabel = config.zones.find((z) =>
        z.tables.some((t) => t.label === value),
      );
      if (byLabel) return byLabel.id;
    }
    return config.zones[0]?.id ?? '';
  }, [config, value, valueById]);

  const [activeZoneId, setActiveZoneId] = useState<string>(initialZone);

  if (!config || config.zones.every((z) => z.tables.length === 0)) {
    return (
      <div className="rounded-md border border-dashed border-border p-6 text-center">
        <ConciergeBell className="mx-auto size-6 text-fg-muted" />
        <p className="mt-2 text-sm text-fg-muted">
          Aún no has configurado las mesas del local.
        </p>
        {readOnly ? null : (
          <Button asChild variant="outline" size="sm" className="mt-3">
            <Link to="/settings">
              <Settings className="size-4" /> Configurar mesas
            </Link>
          </Button>
        )}
      </div>
    );
  }

  const activeZone =
    config.zones.find((z) => z.id === activeZoneId) ?? config.zones[0]!;

  return (
    <div className="space-y-3">
      {config.zones.length > 1 ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-border pb-2">
          {config.zones.map((z) => {
            const active = z.id === activeZone.id;
            return (
              <button
                key={z.id}
                type="button"
                onClick={() => setActiveZoneId(z.id)}
                className={
                  'rounded-md border px-3 py-1.5 text-sm font-semibold transition-colors ' +
                  (active
                    ? 'border-fg bg-fg text-fg-inverse'
                    : 'border-border bg-surface text-fg hover:border-fg')
                }
              >
                {z.name}
              </button>
            );
          })}
        </div>
      ) : null}

      <FloorPlanReadOnly
        zone={activeZone}
        selectedId={valueById ?? null}
        selectedLabel={value}
        readOnly={readOnly}
        busyCountByTableId={busyCountByTableId}
        onSelect={(t) =>
          onSelect?.({
            id: t.id,
            label: t.label,
            capacity: t.capacity,
            zoneName: activeZone.name,
          })
        }
      />
    </div>
  );
};

// ---------- Canvas readonly ----------

const FloorPlanReadOnly = ({
  zone,
  selectedId,
  selectedLabel,
  onSelect,
  readOnly,
  busyCountByTableId,
}: {
  zone: TablesZone;
  /** Si está presente, match exacto por ID (prioridad sobre label). */
  selectedId: string | null;
  selectedLabel: string | null;
  onSelect: (table: TablesTable) => void;
  readOnly: boolean;
  busyCountByTableId: Map<string, number> | undefined;
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    if (!wrapperRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        if (w > 0) setScale(w / VIRTUAL.w);
      }
    });
    observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, []);

  if (zone.tables.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-fg-muted">
        Esta zona no tiene mesas.
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className="relative w-full overflow-hidden rounded-md border border-border bg-surface-muted"
      style={{ aspectRatio: ASPECT_RATIO }}
    >
      <div
        className="absolute left-0 top-0"
        style={{
          width: VIRTUAL.w,
          height: VIRTUAL.h,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          backgroundImage:
            'radial-gradient(circle, var(--color-border) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      >
        {zone.tables.map((t) => {
          // Match por ID si viene (desambigua labels duplicados); si no,
          // fallback a match por label (comportamiento previo, para
          // pedidos históricos sin `table_id`).
          const selected = selectedId
            ? t.id === selectedId
            : selectedLabel === t.label;
          const busyCount = busyCountByTableId?.get(t.id) ?? 0;
          return (
            <FloorTableReadOnly
              key={t.id}
              table={t}
              selected={selected}
              onSelect={() => onSelect(t)}
              readOnly={readOnly}
              busyCount={busyCount}
            />
          );
        })}
      </div>
    </div>
  );
};

const FloorTableReadOnly = ({
  table,
  selected,
  onSelect,
  readOnly,
  busyCount,
}: {
  table: TablesTable;
  selected: boolean;
  onSelect: () => void;
  readOnly: boolean;
  busyCount: number;
}) => {
  const plate = plateSize(table.capacity);
  const counts = chairCounts(table.capacity);
  const box = boundingSize(table.capacity);

  const style: CSSProperties = {
    position: 'absolute',
    left: table.x - box.w / 2,
    top: table.y - box.h / 2,
    width: box.w,
    height: box.h,
    zIndex: selected ? 20 : 10,
  };

  const plateLeft = (box.w - plate.w) / 2;
  const plateTop = (box.h - plate.h) / 2;
  const blocked = table.blocked === true;

  // Renderizamos las sillas como divs absolutos alrededor del plato.
  const offsets = (n: number): number[] =>
    n === 0 ? [] : Array.from({ length: n }, (_, i) => (i + 1) / (n + 1));

  const chairs: CSSProperties[] = [];
  for (const pct of offsets(counts.top)) {
    chairs.push({
      position: 'absolute',
      left: plateLeft + pct * plate.w - CHAIR_LONG / 2,
      top: plateTop - CHAIR_SHORT - CHAIR_GAP,
      width: CHAIR_LONG,
      height: CHAIR_SHORT,
      borderRadius: 6,
    });
  }
  for (const pct of offsets(counts.bottom)) {
    chairs.push({
      position: 'absolute',
      left: plateLeft + pct * plate.w - CHAIR_LONG / 2,
      top: plateTop + plate.h + CHAIR_GAP,
      width: CHAIR_LONG,
      height: CHAIR_SHORT,
      borderRadius: 6,
    });
  }
  for (const pct of offsets(counts.left)) {
    chairs.push({
      position: 'absolute',
      left: plateLeft - CHAIR_SHORT - CHAIR_GAP,
      top: plateTop + pct * plate.h - CHAIR_LONG / 2,
      width: CHAIR_SHORT,
      height: CHAIR_LONG,
      borderRadius: 6,
    });
  }
  for (const pct of offsets(counts.right)) {
    chairs.push({
      position: 'absolute',
      left: plateLeft + plate.w + CHAIR_GAP,
      top: plateTop + pct * plate.h - CHAIR_LONG / 2,
      width: CHAIR_SHORT,
      height: CHAIR_LONG,
      borderRadius: 6,
    });
  }

  const clickable = !blocked && !readOnly;
  const busy = busyCount > 0 && !blocked;
  return (
    <button
      type="button"
      onClick={clickable ? onSelect : undefined}
      disabled={!clickable}
      style={style}
      className={
        'p-0 text-center disabled:cursor-default ' +
        (blocked ? 'opacity-50' : clickable ? 'cursor-pointer' : '')
      }
      title={
        blocked
          ? `Mesa ${table.label} · bloqueada`
          : busy
            ? `Mesa ${table.label} · ${table.capacity} pax · ocupada (${busyCount} ${busyCount === 1 ? 'pedido' : 'pedidos'})`
            : `Mesa ${table.label} · ${table.capacity} pax`
      }
      aria-pressed={selected}
    >
      {chairs.map((s, i) => (
        <div
          key={i}
          style={s}
          className={blocked ? 'bg-fg/10' : 'bg-fg/20'}
          aria-hidden
        />
      ))}
      <div
        className={
          'absolute flex flex-col items-center justify-center text-center shadow-sm ' +
          (plate.round ? 'rounded-full' : 'rounded-lg') +
          ' ' +
          (blocked
            ? 'border-2 border-dashed border-fg-subtle bg-surface-muted'
            : selected
              ? 'border-2 border-accent bg-accent/10 shadow-md'
              : readOnly
                ? 'border border-border bg-surface'
                : 'border border-border bg-surface hover:border-fg')
        }
        style={{
          left: plateLeft,
          top: plateTop,
          width: plate.w,
          height: plate.h,
        }}
      >
        <span
          className={
            'text-lg font-black leading-none tabular-nums ' +
            (blocked ? 'text-fg-subtle line-through' : '')
          }
        >
          {table.label || '—'}
        </span>
        <span className="mt-1 flex items-center gap-1 text-[10px] uppercase tracking-wider text-fg-muted">
          <Users className="size-3" />
          {table.capacity}
        </span>
        {blocked ? (
          <span
            className="absolute -right-1.5 -top-1.5 flex size-5 items-center justify-center rounded-full bg-danger text-white shadow"
            aria-hidden
          >
            <Lock className="size-3" />
          </span>
        ) : busy ? (
          // Badge "N pedidos" en la esquina superior derecha del plato para
          // avisar al operador que la mesa ya tiene tickets activos. El
          // picker sigue permitiendo seleccionarla (legítimo abrir ticket
          // paralelo, ej. bebidas extras).
          <span
            className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-fg-inverse shadow"
            aria-label={`${busyCount} ${busyCount === 1 ? 'pedido activo' : 'pedidos activos'}`}
          >
            {busyCount}
          </span>
        ) : null}
      </div>
      {busy ? (
        <span
          className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full border border-accent/40 bg-accent/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-accent"
          style={{ top: plateTop + plate.h + CHAIR_SHORT + CHAIR_GAP + 4 }}
        >
          Ocupada
        </span>
      ) : null}
    </button>
  );
};
